import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import AuthShell from "@/pages/auth/AuthShell";
import { LegalDialog } from "@/components/LegalDialog";

const LegalLink = ({ label, onOpen, testid }) => (
  <button type="button" data-testid={testid}
    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(); }}
    className="font-semibold text-cyan-dark underline underline-offset-2 hover:text-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan rounded-sm">
    {label}
  </button>
);

const INDUSTRIES = [
  { value: "CONSTRUCTION", label: "Bau" },
  { value: "CARE", label: "Pflege / Ambulanter Pflegedienst" },
  { value: "LOGISTICS", label: "Logistik" },
  { value: "GASTRONOMY", label: "Gastronomie" },
  { value: "CLEANING", label: "Reinigung" },
  { value: "INDUSTRY", label: "Industrie" },
  { value: "SECURITY", label: "Sicherheitsdienst" },
  { value: "OTHER", label: "Sonstige" },
];
const SIZES = ["1–10", "11–50", "51–200", "201–500", "500+"];
const COUNTRIES = ["Germany", "Austria", "Switzerland", "Netherlands", "Other"];

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", password: "", confirm: "",
    company_name: "", company_website: "", industry: "", company_size: "",
    international_employee_count: "", country: "Germany", accept_terms: false, accept_privacy: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [legalDoc, setLegalDoc] = useState(null);
  const acceptDoc = (doc) => set(doc === "terms" ? "accept_terms" : "accept_privacy")(true);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (f.password !== f.confirm) { setError(t("register.mismatch")); return; }
    if (f.password.length < 8) { setError(t("register.tooShort")); return; }
    if (!f.accept_terms || !f.accept_privacy) { setError(t("register.mustAccept")); return; }
    setLoading(true);
    try {
      const { confirm, ...payload } = f;
      const res = await register(payload);
      if (res && res.verify_required) { setSent(true); }
      else { toast.success(t("register.created")); nav("/app/employer", { replace: true }); }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell title={t("vrf.sentTitle")} subtitle={t("vrf.sentSub", { email: f.email })} testid="register-verify-sent"
        footer={<Link to="/login" className="font-semibold text-cyan-dark hover:underline">{t("register.login")}</Link>}>
        <div className="rounded-xl bg-cyan/5 p-4 text-sm text-slate-600" data-testid="verify-sent-msg">{t("vrf.sentBody")}</div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("register.title")} subtitle={t("register.subtitle")} testid="register-page"
      footer={<>{t("register.already")} <Link to="/login" className="font-semibold text-cyan-dark hover:underline">{t("register.login")}</Link></>}>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>{t("register.firstName")}</Label><Input required value={f.first_name} onChange={(e) => set("first_name")(e.target.value)} data-testid="reg-firstname" /></div>
          <div className="space-y-2"><Label>{t("register.lastName")}</Label><Input required value={f.last_name} onChange={(e) => set("last_name")(e.target.value)} data-testid="reg-lastname" /></div>
        </div>
        <div className="space-y-2"><Label>{t("register.email")}</Label><Input type="email" required value={f.email} onChange={(e) => set("email")(e.target.value)} data-testid="reg-email" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>{t("register.password")}</Label><PasswordInput required value={f.password} onChange={(e) => set("password")(e.target.value)} data-testid="reg-password" /></div>
          <div className="space-y-2"><Label>{t("register.confirm")}</Label><PasswordInput required value={f.confirm} onChange={(e) => set("confirm")(e.target.value)} data-testid="reg-confirm" /></div>
        </div>
        <div className="space-y-2"><Label>{t("register.company")}</Label><Input required value={f.company_name} onChange={(e) => set("company_name")(e.target.value)} data-testid="reg-company" /></div>
        <div className="space-y-2"><Label>{t("register.website")} <span className="text-slate-400">{t("register.optional")}</span></Label><Input value={f.company_website} onChange={(e) => set("company_website")(e.target.value)} data-testid="reg-website" placeholder="https://" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>{t("register.industry")}</Label>
            <Select value={f.industry} onValueChange={set("industry")}>
              <SelectTrigger data-testid="reg-industry"><SelectValue placeholder={t("register.select")} /></SelectTrigger>
              <SelectContent>{INDUSTRIES.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t("register.size")}</Label>
            <Select value={f.company_size} onValueChange={set("company_size")}>
              <SelectTrigger data-testid="reg-size"><SelectValue placeholder={t("register.select")} /></SelectTrigger>
              <SelectContent>{SIZES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>{t("register.intl")}</Label><Input value={f.international_employee_count} onChange={(e) => set("international_employee_count")(e.target.value)} data-testid="reg-intl" placeholder="e.g. 25" /></div>
          <div className="space-y-2">
            <Label>{t("register.country")}</Label>
            <Select value={f.country} onValueChange={set("country")}>
              <SelectTrigger data-testid="reg-country"><SelectValue /></SelectTrigger>
              <SelectContent>{COUNTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-3 rounded-xl bg-slate-50 p-4">
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <Checkbox checked={f.accept_terms} onCheckedChange={set("accept_terms")} data-testid="reg-terms" className="mt-0.5" />
            <span>{t("register.acceptTermsPre")} <LegalLink label={t("register.termsLink")} onOpen={() => setLegalDoc("terms")} testid="reg-terms-link" /></span>
          </label>
          <label className="flex items-start gap-3 text-sm text-slate-600">
            <Checkbox checked={f.accept_privacy} onCheckedChange={set("accept_privacy")} data-testid="reg-privacy" className="mt-0.5" />
            <span>{t("register.acceptPrivacyPre")} <LegalLink label={t("register.privacyLink")} onOpen={() => setLegalDoc("privacy")} testid="reg-privacy-link" /></span>
          </label>
        </div>
        <LegalDialog doc={legalDoc} onClose={() => setLegalDoc(null)} onAccept={acceptDoc} />
        {error && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="reg-error">{error}</div>}
        <Button type="submit" disabled={loading} data-testid="register-submit-button" className="w-full rounded-full bg-cyan text-white hover:bg-cyan-dark">
          {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("register.creating")}</> : t("register.submit")}
        </Button>
      </form>
    </AuthShell>
  );
}
